import { AnimatePresence, motion } from 'motion/react';
import React from 'react';
import { useLocation } from 'react-router-dom';
import PixelIcon from './PixelIcon';
import { FONT, FONT_SIZE, SPACING, RADIUS, COLOR, SHADOW, VARIANT_COLORS, Z_INDEX, TOUCH } from '@/config/designTokens';

export interface MobileNavBarProps {
  actions: Array<{
    id: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
    shortcut?: string;
  }>;
  className?: string;
}

// Button variant styles using design tokens
const getVariantStyles = (variant: string = 'secondary') => {
  const vc = VARIANT_COLORS[variant] || VARIANT_COLORS.secondary;
  return {
    gradient: vc.bgGradient,
    border: vc.border,
    shadow: `rgba(${variant === 'primary' ? '78, 205, 196' : variant === 'success' ? '39, 174, 96' : variant === 'warning' ? '243, 156, 18' : variant === 'danger' ? '231, 76, 60' : variant === 'info' ? '155, 89, 182' : '69, 183, 209'}, 0.3)`,
  };
};

const MobileNavBar: React.FC<MobileNavBarProps> = ({ actions, className = '' }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const columnCount = Math.min(Math.max(actions.length, 1), 5);

  if (!isHome) {
    return null;
  }

  return (
    <motion.nav
      className={`mobile-nav-bar ${className}`}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: Z_INDEX.mobileNav,
        fontFamily: FONT.heading,
        padding: '0',
      }}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
        duration: 0.6,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
          minHeight: '90px',
          gap: SPACING.xs,
          padding: '0px 0px 0px',
          margin: '0 auto',
        }}
      >
        <AnimatePresence>
          {actions.map((action, index) => {
            const variantStyle = getVariantStyles(action.variant);

            return (
              <motion.button
                key={action.id}
                layoutId={`nav-button-${action.id}`}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled}
                className={`mobile-nav-button mobile-nav-button--${action.variant || 'secondary'}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.md,
                  minHeight: TOUCH.navMinHeight,
                  width: '100%',
                  padding: `${SPACING.md} ${SPACING.sm} ${SPACING.sm}`,
                  background: variantStyle.gradient,
                  border: `3px solid ${variantStyle.border}`,
                  borderRadius: RADIUS.navButton,
                  borderBottom: '0',
                  color: COLOR.textWhite,
                  fontSize: FONT_SIZE.navLabel,
                  fontWeight: 'normal',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  fontFamily: 'inherit',
                  cursor: action.disabled ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  userSelect: 'none',
                  outline: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  boxShadow: `${SHADOW.navButton}, 0 0 0 2px ${variantStyle.border}`,
                  opacity: action.disabled ? 0.6 : 1,
                  transition: 'none',
                }}
                initial={{
                  opacity: 0,
                  scale: 0.8,
                  y: 50,
                }}
                animate={{
                  opacity: action.disabled ? 0.6 : 1,
                  scale: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                  y: 50,
                }}
                whileHover={
                  action.disabled
                    ? {}
                    : {
                        y: -3,
                        boxShadow: `
                    0 12px 30px rgba(0, 0, 0, 0.4),
                    inset 0 3px 6px rgba(255, 255, 255, 0.3),
                    inset 0 -2px 4px rgba(0, 0, 0, 0.3),
                    0 0 20px ${variantStyle.shadow}
                  `,
                      }
                }
                whileTap={
                  action.disabled
                    ? {}
                    : {
                        scale: 0.95,
                        y: 0,
                      }
                }
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 25,
                  delay: index * 0.05,
                }}
              >
                {/* Glow effect layer */}
                <motion.div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(circle at center, rgba(255, 255, 255, 0.2) 0%, transparent 70%)',
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                  animate={
                    action.disabled
                      ? {}
                      : {
                          opacity: [0, 0.3, 0],
                        }
                  }
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: index * 0.3,
                  }}
                />

                {/* Icon container */}
                <motion.div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4))',
                  }}
                  whileHover={action.disabled ? {} : { scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  {action.icon}
                </motion.div>

                {/* Label */}
                <span
                  style={{
                    textAlign: 'center',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textShadow: SHADOW.text.replace('2px', '1px'),
                  }}
                >
                  {action.label}
                </span>

                {/* Disabled overlay */}
                <AnimatePresence>
                  {action.disabled && (
                    <motion.div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: COLOR.disabledOverlay,
                        borderRadius: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <PixelIcon name="lock" size={16} color={COLOR.disabled} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default MobileNavBar;
